import { reportConfigurationsSelectors } from 'app-components/flux/report-configurations';
import { getReportDisplayName } from 'app-components/flux/report-configurations/reportConfigurationsConstants';
import type {
  ReportConfiguration,
  ReportNameType,
} from 'app-components/flux/report-configurations/reportConfigurationsTypes';
import { getLogger } from 'app-components/utils/core';
import type RootState from 'app-components/utils/redux-store/rootState';
import { filterSavedReports } from 'containers/OpenSaveReports/components/home/filterSavedReports';

const log = getLogger('reportIntents.savedReports');

export type ReportIntentArgs = {
  savedReportName?: string;
};

export type SavedReportMatchResult =
  | { type: 'found'; config: ReportConfiguration }
  | { type: 'not_found' }
  | { type: 'ambiguous'; names: string[] };

const MAX_LOGGED_SAVED_REPORT_CONFIGS = 50;

const toSavedReportConfigLog = (config: ReportConfiguration): Record<string, unknown> => ({
  id: config.id,
  persistentType: config.persistentType,
  source: config.source,
  name: config.name,
  predefinedConfigId: config.predefinedConfigId,
  descriptionLength: config.description?.length ?? 0,
});

const limitStringForLog = (value: string, maxLength: number): string => {
  if (value.length <= maxLength) return value;
  return `${value.slice(0, maxLength)}…`;
};

export const getSavedReportDisplayName = (config: ReportConfiguration): string =>
  config.description || getReportDisplayName(config.name, config.predefinedConfigId);

/**
 * If the "candidate" looks like the generic report name (e.g. "spending"),
 * skip the saved-report lookup so generic navigation still works as expected.
 */
const looksLikeGenericReportName = (candidate: string, reportNames?: readonly ReportNameType[]): boolean => {
  const normalizedCandidate = candidate.trim().toLowerCase();
  if (!normalizedCandidate) return false;

  const genericDisplayNames = (reportNames ?? [])
    .map((reportName) => getReportDisplayName(reportName).trim().toLowerCase())
    .filter(Boolean);

  return genericDisplayNames.includes(normalizedCandidate);
};

const getSavedReportName = (
  intentArgs: ReportIntentArgs | undefined,
  reportNames?: readonly ReportNameType[],
): string | undefined => {
  // We intentionally rely on the LLM to pass the correct saved report title via `savedReportName`.
  const savedReportName = intentArgs?.savedReportName?.trim();
  if (!savedReportName) return undefined;

  if (looksLikeGenericReportName(savedReportName, reportNames)) return undefined;

  return savedReportName;
};

const findSavedReportMatch = (state: RootState, requestedName: string): SavedReportMatchResult => {
  const savedConfigs = reportConfigurationsSelectors.selectSavedReportsConfigs(state);
  if (!savedConfigs.length) {
    log.debug('No saved report configs available when resolving saved report navigation.');
    return { type: 'not_found' };
  }

  // filterSavedReports already logs its inputs; this is a high-level marker for this entry point.
  log.debug('Resolving saved report navigation via filterSavedReports.', {
    query: limitStringForLog(requestedName, 200),
    queryLength: requestedName.length,
    savedConfigsCount: savedConfigs.length,
    savedConfigs: savedConfigs.slice(0, MAX_LOGGED_SAVED_REPORT_CONFIGS).map(toSavedReportConfigLog),
    savedConfigsTruncated: savedConfigs.length > MAX_LOGGED_SAVED_REPORT_CONFIGS,
  });

  const fuseMatches = filterSavedReports(requestedName, savedConfigs).map((result) => result.config);
  if (fuseMatches.length === 1) {
    return { type: 'found', config: fuseMatches[0] };
  }

  if (fuseMatches.length > 1) {
    return { type: 'ambiguous', names: fuseMatches.map(getSavedReportDisplayName) };
  }

  log.debug('filterSavedReports produced no matches for saved report navigation.', {
    requestedNameLength: requestedName.length,
    savedConfigsCount: savedConfigs.length,
  });

  return { type: 'not_found' };
};

export type SavedReportResolution =
  | { type: 'skip' }
  | { type: 'ambiguous'; candidateName: string; names: string[] }
  | { type: 'found'; config: ReportConfiguration }
  | { type: 'explicit_not_found'; candidateName: string };

/**
 * Attempt to resolve a saved report from the intent args.
 *
 * - We do not attempt to parse `userQuery` into a report title. That responsibility is left to the LLM,
 *   which should pass the cleaned title via the `savedReportName` param.
 */
export const resolveSavedReportFromIntentArgs = (
  state: RootState,
  intentArgs: ReportIntentArgs | undefined,
  reportNames?: readonly ReportNameType[],
): SavedReportResolution => {
  const candidateName = getSavedReportName(intentArgs, reportNames);
  if (!candidateName) return { type: 'skip' };

  const match = findSavedReportMatch(state, candidateName);
  if (match.type === 'found') {
    return { type: 'found', config: match.config };
  }

  if (match.type === 'ambiguous') {
    return { type: 'ambiguous', candidateName, names: match.names };
  }

  // When the LLM provides `savedReportName`, we treat a miss as an explicit "not found"
  // so `report_saved` (and other callers that pass `savedReportName`) can surface a clear error.
  return { type: 'explicit_not_found', candidateName };
};
